import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Tab, Button } from "semantic-ui-react";
import {
  editOrganization,
  editOrganizationsContact,
  removeOrganizationsContact,
  createOrganizationsContact,
  processRawLimitsAndAccounting,
  remapRoleNameFromRoleName,
  inviteUserToAccount
} from "../../actions/accounts/accounts";
import { formatDateAsShortDate } from "../../actions/dateTimeFormats/dateTimeFormats";
import { getRegionSettings } from "../../actions/regions/regionSettings";
import {
  handleChange,
  saveChanges,
  resetChanges
} from "../../actions/rolesPermissions/rolesPermissions";
import {
  getSystemsFromUserRoles,
  handleCancelInvite,
  handleReinviteUser,
  handleDeleteUser,
  handleSaveUserRoles,
  isExternal
} from "../../actions/users/users";
import { amRoutes } from "../../config/config";
import { hiddenAclResources } from "../../config/featureFlags";
import { AppDispatch, RootState } from "../../reducers";
import { history } from "../../store";
import { ContactInformation, Organization } from "../../types/organizations";
import { exists, isAdministrator, isModeler } from "../../utils/utils";
import ConfirmationPrompt from "../common/ConfirmationPrompt";
import RegionsTab from "./tabs/regions/RegionsTab";
import RolesPermissions from "./tabs/roles/RolesPermissions";
import SecurityTab from "./tabs/security/SecurityTab";
import SettingsTab from "./tabs/settings/Tab";
import Subscriptions from "./tabs/subscription/Subscription";
import UsersRoles from "./tabs/users/UsersRoles";

const mapStateToProps = ({
  users,
  rolesPermissions,
  accounts,
  statusMessages,
  publicRoles,
  regionSettings
}: RootState) => ({
  aclList: users.aclList,
  availableRegions: users.availableRegions,
  publicRegions: publicRoles,
  changes: rolesPermissions.changes,
  fineGranularityChanges: rolesPermissions.fineGranularityChanges,
  organization: accounts.organization,
  user: users.userClaims,
  limitsAndAccounting:
    exists(accounts.limits) && exists(accounts.accounting)
      ? processRawLimitsAndAccounting(accounts.limits, accounts.accounting)
      : {},
  userSystems: getSystemsFromUserRoles(users.userClaims.roles ?? []),
  users: accounts.users,
  showSuccessMessage: statusMessages.showSuccessMessage,
  submitSucceededMessageHeader: statusMessages.submitSucceededMessageHeader,
  submitSucceededMessageContent: statusMessages.submitSucceededMessageContent,
  showFailureMessage: statusMessages.showFailureMessage,
  submitFailedMessageHeader: statusMessages.submitFailedMessageHeader,
  submitFailedMessageContent: statusMessages.submitFailedMessageContent,
  startProjects: accounts.pencilerStartProjectsStatus,
  gettingUsers: accounts.gettingUsers,
  administrators: accounts.administrators,
  gettingStartProjects: accounts.gettingPencilerStartProjectsStatus,
  getStartProjectsSuccess: accounts.getPencilerStartProjectsStatusSuccess,
  getStartProjectsError: accounts.getPencilerStartProjectsStatusError,
  getStartProjectsErrorMessage: accounts.getPencilerStartProjectsStatusErrorMessage,
  inviteUserToAccountModalFailed: accounts.inviteUserToAccountModalFailed,
  inviteUserToAccountModalFailedMessage: accounts.inviteUserToAccountModalFailedMessage,
  roles: accounts.roles,
  permissions: accounts.permissions,
  fineGrainedPermissions: accounts.fineGrainedPermissions,
  regionSettings: regionSettings
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  editOrganization: (organization: Partial<Organization>) =>
    dispatch(editOrganization(organization)),
  editOrganizationContact: (id: number, contact: ContactInformation) =>
    dispatch(editOrganizationsContact(id, contact)),
  removeOrganizationsContact: (id: number) => dispatch(removeOrganizationsContact(id)),
  createOrganizationContact: (contact: ContactInformation) =>
    dispatch(createOrganizationsContact(contact)),
  onChange: (change: any) => dispatch(handleChange(change)),
  // @ts-ignore: Unused argument but this is not used
  onSaveChanges: change => dispatch(saveChanges(change)),
  onResetChanges: () => dispatch(resetChanges()),
  onEditUser: (id: number, roles: number[]) => dispatch(handleSaveUserRoles(id, roles)),
  onDeleteUser: (id: number) => dispatch(handleDeleteUser(id)),
  onCancelInvite: (id: number) => dispatch(handleCancelInvite(id)),
  onReinviteUser: (id: number) => dispatch(handleReinviteUser(id)),
  // Unused
  remapRoleNameFromRoleName: (roleName: string) => remapRoleNameFromRoleName(roleName),
  inviteUserToAccount: ({
    firstName,
    lastName,
    email,
    roleCode
  }: {
    firstName: string;
    lastName: string;
    email: string;
    roleCode: string;
  }) => {
    dispatch(inviteUserToAccount(firstName, lastName, email, roleCode));
  },
  isExternal: () => dispatch(isExternal()),
  getRegionSettings: () => dispatch(getRegionSettings()),
  formatDateAsShortDate: (date: Date | string) => dispatch(formatDateAsShortDate(date))
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type ManagementProps = { activeTab?: string } & PropsFromRedux;
type ManagementState = { rolesPermissionsKey: number };

class Management extends React.Component<ManagementProps, ManagementState> {
  constructor(props: ManagementProps) {
    super(props);
    this.state = { rolesPermissionsKey: 0 };
  }
  handleTabChange = (_e: any, { activeIndex }: { activeIndex: number }) => {
    const renderedPanes = this.panes.filter(pane => pane.shouldRender);
    history.push(renderedPanes[activeIndex].url);
  };

  changesOcurred = () =>
    this.props.changes.length === 0 && this.props.fineGranularityChanges.length === 0;

  panes = [
    {
      name: "subscription",
      menuItem: "Subscription",
      url: amRoutes.managementSubscription,
      shouldRender: true,
      render: () => (
        <Tab.Pane attached={false} className="management-tab-pane" as="Segment">
          <Subscriptions
            organization={this.props.organization}
            user={this.props.user}
            canEdit={isAdministrator(this.props.user)}
            canRemove={isAdministrator(this.props.user)}
            canManage={isAdministrator(this.props.user)}
            canAdd={isAdministrator(this.props.user)}
            users={this.props.users}
            canViewRuns={isModeler(this.props.user) || isAdministrator(this.props.user)}
            limitsAndAccounting={this.props.limitsAndAccounting}
            userSystems={this.props.userSystems}
            showSuccessMessage={this.props.showSuccessMessage}
            submitSucceededMessageHeader={this.props.submitSucceededMessageHeader}
            submitSucceededMessageContent={this.props.submitSucceededMessageContent}
            editOrganization={this.props.editOrganization}
            editOrganizationContact={this.props.editOrganizationContact}
            removeOrganizationsContact={this.props.removeOrganizationsContact}
            createOrganizationContact={this.props.createOrganizationContact}
            startProjects={this.props.startProjects}
            gettingStartProjects={this.props.gettingStartProjects}
            getStartProjectsSuccess={this.props.getStartProjectsSuccess}
            getStartProjectsError={this.props.getStartProjectsError}
            getStartProjectsErrorMessage={this.props.getStartProjectsErrorMessage}
            disabled={this.props.isExternal()}
            isExternal={this.props.isExternal()}
          />
        </Tab.Pane>
      )
    },
    {
      name: "users",
      menuItem: "Users",
      shouldRender: isAdministrator(this.props.user),
      url: amRoutes.managementUsers,
      render: () => (
        <Tab.Pane attached={false} className="management-tab-pane" as="Segment">
          <UsersRoles
            regionDB={this.props.user.selectedRegion?.database}
            canInvite={isAdministrator(this.props.user)}
            canEdit={isAdministrator(this.props.user)}
            onInvite={this.props.inviteUserToAccount}
            // Commenting unused props
            // inviteSucceeded={this.props.showSuccessMessage}
            // inviteSucceededMessage={this.props.submitSucceededMessageHeader}
            // inviteFailed={this.props.inviteUserToAccountModalFailed}
            // inviteFailedMessage={this.props.inviteUserToAccountModalFailedMessage}
            user={this.props.user}
            users={this.props.users}
            roles={this.props.roles.filter(
              role => role.regionId === this.props.user.selectedRegionId
            )}
            onEditUser={this.props.onEditUser}
            onDeleteUser={this.props.onDeleteUser}
            onCancelInvite={this.props.onCancelInvite}
            onReinviteUser={this.props.onReinviteUser}
            disabled={this.props.isExternal()}
            isExternal={this.props.isExternal()}
            formatDateAsShortDate={this.props.formatDateAsShortDate}
          />
        </Tab.Pane>
      )
    },
    {
      name: "permissions",
      menuItem: "Permissions",
      shouldRender: true,
      url: amRoutes.managementPermissions,
      render: () => (
        <>
          <Tab.Pane attached={false} className="management-tab-pane" as="Segment">
            <RolesPermissions
              regionDB={this.props.user.selectedRegion?.database}
              administrators={this.props.administrators}
              user={this.props.user}
              canEdit={isAdministrator(this.props.user)}
              key={this.state.rolesPermissionsKey}
              onChange={this.props.onChange}
              permissions={this.props.permissions.filter(
                ({ aclResource }) => !hiddenAclResources.includes(aclResource?.code ?? "")
              )}
              fineGrainedPermissions={this.props.fineGrainedPermissions}
              roles={this.props.roles.filter(
                role => role.regionId === this.props.user.selectedRegionId
              )}
              isExternal={this.props.isExternal()}
            />
          </Tab.Pane>
          {isAdministrator(this.props.user) && (
            <Button.Group style={{ display: "block" }}>
              <ConfirmationPrompt
                trigger={
                  <Button disabled={this.changesOcurred()} color="green">
                    Save Changes
                  </Button>
                }
                content={"Are you sure you want to save changes?"}
                headerIcon={"save outline"}
                onConfirmation={() => this.props.onSaveChanges(this.props.changes)}
              />
              <ConfirmationPrompt
                trigger={
                  <Button disabled={this.changesOcurred()} color="red">
                    Discard Changes
                  </Button>
                }
                buttonLabel={"Discard Changes"}
                buttonColor={"red"}
                content={"Are you sure you want to discard changes?"}
                headerIcon={"undo"}
                onConfirmation={() => {
                  this.props.onResetChanges();
                  this.setState({ rolesPermissionsKey: this.state.rolesPermissionsKey + 1 });
                }}
              />
            </Button.Group>
          )}
        </>
      )
    },
    {
      name: "regions",
      menuItem: "Regions",
      shouldRender: true,
      url: amRoutes.managementRegions,
      render: () => (
        <RegionsTab disabled={this.props.isExternal()} isExternal={this.props.isExternal()} />
      )
    },
    {
      name: "settings",
      menuItem: "Settings",
      shouldRender: true,
      url: amRoutes.managementSettings,
      render: () => {
        return (
          <Tab.Pane attached={false} className="management-tab-pane" as="Segment">
            <SettingsTab />
          </Tab.Pane>
        );
      }
    },
    {
      name: "security",
      menuItem: "Security",
      shouldRender: true,
      url: amRoutes.managementSecurity,
      render: () => {
        return <SecurityTab isExternal={this.props.isExternal()} />;
      }
    }
  ];

  render() {
    const renderedPanes = this.panes.filter(pane => pane.shouldRender);
    const index = renderedPanes.findIndex(pane => pane.name === this.props.activeTab);
    return (
      <>
        <Tab
          activeIndex={index >= 0 ? index : 0}
          menu={{ secondary: true, pointing: true }}
          // @ts-ignore: activeIndex is always a number
          onTabChange={this.handleTabChange}
          panes={renderedPanes}
        />
      </>
    );
  }
}

export default connector(Management);
